import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Box,
  alpha,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { GfImage } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../locales/messages";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { getAiPromptSetting } from "../../../utils/aiImageImprovement";

const BRAND = "#f58220";
const IMAGE_BG = "#fafafa";

const getImagePreviewSrc = (img: GfImage): string =>
  img.src ?? img.srcSmall ?? "";

interface Props {
  open: boolean;
  images: GfImage[];
  onClose: () => void;
  onStart: (prompt: string, imageId: string) => void;
  isStarting?: boolean;
}

export const AiImproveImageModal: React.FC<Props> = ({
  open,
  images,
  onClose,
  onStart,
  isStarting = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const authHeaders = useAuthHeaders();
  const authToken = authHeaders.headers?.Authorization ?? "";
  const [prompt, setPrompt] = useState("");
  const [effectivePrompt, setEffectivePrompt] = useState("");
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);

  const selectableImages = useMemo(
    () => images.filter((img) => img?.id && Boolean(getImagePreviewSrc(img))),
    [images],
  );

  const selectedImage = useMemo(
    () => selectableImages.find((img) => img.id === selectedImageId),
    [selectableImages, selectedImageId],
  );

  const hasMultiple = selectableImages.length > 1;

  useEffect(() => {
    if (!open) return;
    setPrompt("");
    const withIds = images.filter(
      (img) => img?.id && Boolean(getImagePreviewSrc(img)),
    );
    setSelectedImageId((prev) => {
      if (prev && withIds.some((img) => img.id === prev)) return prev;
      return withIds[0]?.id ?? null;
    });
    setLoadingSettings(true);
    getAiPromptSetting({ headers: { Authorization: authToken } })
      .then((s) => setEffectivePrompt(s.effective))
      .catch(console.error)
      .finally(() => setLoadingSettings(false));
  }, [open, authToken, images]);

  const handleStart = () => {
    if (!selectedImageId) return;
    onStart(prompt.trim(), selectedImageId);
  };

  return (
    <Dialog
      open={open}
      onClose={isStarting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 1600 }}
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          m: { xs: 1, sm: 3 },
          maxHeight: { xs: "95vh", sm: "min(480px, 85vh)" },
          height: { sm: "min(480px, 85vh)" },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      data-testid="ai-improve-image-modal"
    >
      <DialogTitle
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1.25,
          px: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <AutoAwesomeIcon sx={{ color: BRAND, fontSize: 20 }} />
        <Typography
          variant="subtitle1"
          component="span"
          fontWeight={600}
          sx={{ flex: 1 }}
        >
          {t(messages.improveWithAiModalTitle())}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={isStarting}
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 0,
          p: 0,
          "&.MuiDialogContent-root": { p: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: isDesktop ? "0 0 280px" : "0 0 auto",
            borderRight: isDesktop ? "1px solid" : "none",
            borderBottom: isDesktop ? "none" : "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: isDesktop ? 0 : 160,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              bgcolor: IMAGE_BG,
            }}
          >
            {selectedImage ? (
              <Box
                component="img"
                src={getImagePreviewSrc(selectedImage)}
                alt={selectedImage.alt || selectedImage.fileName || ""}
                data-testid="ai-improve-selected-preview"
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  display: "block",
                  borderRadius: 1,
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(messages.noImagesSelected())}
              </Typography>
            )}
          </Box>

          {hasMultiple && (
            <Box
              sx={{
                flexShrink: 0,
                px: 1.5,
                py: 1,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 0.75,
                  overflowX: "auto",
                  pb: 0.5,
                  "&::-webkit-scrollbar": { height: 4 },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "#ddd",
                    borderRadius: 2,
                  },
                }}
                data-testid="ai-improve-image-picker"
              >
                {selectableImages.map((img) => {
                  const isSelected = selectedImageId === img.id;
                  return (
                    <Box
                      key={img.id}
                      onClick={() => !isStarting && setSelectedImageId(img.id!)}
                      data-testid={`ai-improve-image-option-${img.id}`}
                      sx={{
                        position: "relative",
                        width: 48,
                        height: 48,
                        minWidth: 48,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: IMAGE_BG,
                        borderRadius: 0.75,
                        border: "2px solid",
                        borderColor: isSelected ? BRAND : "transparent",
                        cursor: isStarting ? "default" : "pointer",
                        overflow: "hidden",
                        transition: "border-color 0.15s",
                        "&:hover": !isStarting
                          ? { borderColor: isSelected ? BRAND : "#ccc" }
                          : undefined,
                      }}
                    >
                      <Box
                        component="img"
                        src={getImagePreviewSrc(img)}
                        alt={img.alt || img.fileName || ""}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                      {isSelected && (
                        <CheckCircleIcon
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            color: BRAND,
                            fontSize: 14,
                            bgcolor: "white",
                            borderRadius: "50%",
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            p: 2.5,
            overflow: isDesktop ? "hidden" : "auto",
          }}
        >
          {loadingSettings ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CircularProgress size={16} sx={{ color: BRAND }} />
              <Typography variant="caption" color="text.secondary">
                {t(messages.Loading())}
              </Typography>
            </Box>
          ) : effectivePrompt ? (
            <Box
              sx={{
                mb: 2,
                px: 1.5,
                py: 1,
                bgcolor: alpha(BRAND, 0.04),
                borderRadius: 1.5,
                borderLeft: `3px solid ${BRAND}`,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                display="block"
                sx={{ mb: 0.25 }}
              >
                {t(messages.improveWithAiSavedPromptPreview())}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.4,
                  color: "text.primary",
                  fontSize: "0.8rem",
                }}
                title={effectivePrompt}
              >
                {effectivePrompt}
              </Typography>
            </Box>
          ) : null}

          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            {t(messages.improveWithAiPromptSectionTitle())}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, lineHeight: 1.3 }}
          >
            {t(messages.improveWithAiPromptHint())}
          </Typography>

          <TextField
            multiline
            fullWidth
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t(messages.improveWithAiPromptPlaceholder())}
            inputProps={{ maxLength: 2000 }}
            disabled={isStarting}
            data-testid="ai-improve-prompt-input"
            sx={{
              flex: 1,
              minHeight: 0,
              "& .MuiOutlinedInput-root": {
                height: "100%",
                alignItems: "flex-start",
                borderRadius: 1.5,
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: BRAND,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha(BRAND, 0.5),
                },
              },
              "& .MuiInputBase-inputMultiline": {
                height: "100% !important",
                overflow: "auto !important",
              },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, textAlign: "right", flexShrink: 0 }}
          >
            {prompt.length}/2000
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          flexShrink: 0,
          px: 2.5,
          py: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isStarting}
          variant="text"
          color="inherit"
          size="small"
          sx={{ textTransform: "none", fontWeight: 500 }}
        >
          {t(messages.Cancel())}
        </Button>
        <Button
          variant="contained"
          size="medium"
          onClick={handleStart}
          disabled={isStarting || !selectedImageId}
          data-testid="ai-improve-start-button"
          startIcon={
            isStarting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            )
          }
          sx={{
            bgcolor: BRAND,
            textTransform: "none",
            fontWeight: 600,
            px: 2.5,
            borderRadius: 1.5,
            "&:hover": { bgcolor: "#e07410" },
            "&.Mui-disabled": {
              bgcolor: alpha(BRAND, 0.4),
              color: "white",
            },
          }}
        >
          {isStarting
            ? t(messages.processing())
            : t(messages.startAiImprovement())}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
